/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type ValidationErrors } from '@angular/forms';
import { type FormControl, I18nService } from '@dm/core';
import { ProductConfig } from 'app/config';
import { FeatureConfig } from 'app/config/feature';

import { type Privilege } from './permit-table/permit-table';

/**
 * 用户组成员账户类型
 */
export enum WindowsGroupMemberTypeE {
    WINDOWS_USER = '0',
    WINDOWS_USER_GROUP = '1',
    AD_USER = '2',
    AD_USER_GROUP = '3'
}

/**
 * rid范围
 */
export const ridRange = {
    min: 1000,
    max: 4294967295,
};

/**
 * 特权
 */
export enum PrivilegeE {
    None = '0',
    SeSecurityPrivilege = '1'
}

/**
 * 用户组名校验
 */
export function nameFormatValidator(control: FormControl): ValidationErrors {
    // eslint-disable-next-line no-control-regex
    const rule = /[\"\/\\\]\[\:\;\|\=\,\+\*\?\<\>\@\x00-\x1F\x7F-\x9F]+/;
    const value: string = control.value;

    // 1、不能为"/\][:;|=,+*?<>@、控制字符
    if (rule.test(value)) {
        return { nameFormat: true };
    }

    // 2、不能以.结尾
    if (value.endsWith('.')) {
        return { nameFormat: true };
    }

    return null;
}

/**
 * ad用户 用户组名校验
 */
export function adNameFormatValidator(control: FormControl): ValidationErrors {
    if (!control.value) {
        return null;
    }
    const valueList = control.value.split('\\');
    const isValid = valueList.length === 2 && valueList.every((item) => item.trim());

    return isValid ? null : { nameFormat: true };
}

@Injectable({ providedIn: 'root' })
export class WindowsAccessUserGroupStrService {
    get = this.i18nService.get;

    i18n = {
        // 按钮
        yes: this.get('common.yes_lab'),
        no: this.get('common.no_lab'),
        ok: this.get('common.confirm_button'),
        cancel: this.get('common.cancel_button'),
        close: this.get('common.close_button'),
        createBtn: this.get('common.create_button'),
        addBtn: this.get('common.add_button'),
        deleteBtn: this.get('common.delete_button'),
        removeBtn: this.get('common.remove_button'),
        modifyBtn: this.get('common.modify_button'),
        operationBtn: this.get('common.operation_button'),
        basicInfoLab: this.get('common.basicInfo_title'),
        member: this.get('common.member_title'),
        infoLabel: this.get('common.task_collectInfo_title_label'),
        groupType: this.get('common.type_column'),

        name: this.get('common.name_column'),
        vStoreLab: this.get('common.owningVstore_lab'),
        description: this.get('common.description_column'),
        rid: this.get('RID'),
        operation: this.get('common.operation_button'),
        localAuthGroup: this.i18nService.get('service.accessUser_localAuthUserGroup_lab'),
        localAuthUser: this.get('service.accessUser_term_localAuthUser_lab'),
        adUser: this.get('service.accessUserGroup_task_view_ADUser_tab'),
        adUserGroup: this.get('service.accessUserGroup_task_view_ADUserGroup_tab'),
        disableOperateUserTip: this.get('service.accessUserGroup_task_modfiyAndDel_error_tip'),
        /**
         * 特权
         */
        privilege: this.get('service.accessUser_term_localWinAuthUser_privilege_lab'),
        /**
         * 已选择的特权
         */
        selectedObj: this.get('service.accessUser_term_localWinAuthUser_availPrivilege_lab'),
        /**
         * 管理审核，包括查看、转储和清空安全日志。
         */
        seSecurityDes: this.get('service.accessUser_term_SeSecurityPrivilege_desc'),
        // 防病毒扫描
        SeAntiVirusPrivilege: this.get('service.accessUser_term_SeAntiVirusPrivilege_desc'),

        // 添加ad域用户、用户组中表单label
        adUserName: this.get('common.name_column'),
        adUserGroupName: this.get('common.name_column'),
        leftGroupTitle: this.get('common.common_available_label'),
        rightGroupTitle: this.get('common.common_selected_label'),

        // modal title
        deleteGroupTitle: this.get('service.accessUser_term_localWinAuthUserGroup_delete_title'),
        createGroupTitle: this.get('service.accessUser_term_localWinAuthUserGroup_create_title'),
        modifyGroupTitle: this.get('service.accessUser_term_localWinAuthUserGroup_modify_title'),
        addAdUserTitle: this.get('service.accessUserGroup_task_view_addADUser_title'),
        addAdUserGroupTitle: this.get('service.accessUserGroup_task_view_addADUserGroup_title'),
        addLocalUserTitle: this.get('service.accessUserGroup_task_view_addLocalUser_title'),
        removeAdUserMemberTitle: this.get('service.accessUserGroup_task_view_removeADUser_title'),
        removeAdUserGroupMemberTitle: this.get('service.accessUserGroup_task_view_removeADUserGroup_title'),
        removeLocalUserMemberTitle: this.get('service.accessUserGroup_task_view_removeLocalUser_title'),

        // 二次确认
        deleteGroupMsg: this.get('service.accessUser_task_deleteWinGroup_confirm_msg'),
        removeAdUserMemberMsg: this.get('service.accessUser_task_removeADUser_fromWinGroup_confirm_msg'),
        removeAdUserGroupMemberMsg: this.get('service.accessUser_task_removeADGroup_fromWinGroup_confirm_msg'),
        removeLocalUserMemberMsg: this.get('service.accessUser_task_removeWinUser_fromWinGroup_confirm_msg'),

        adMultiNameTip: this.get('service.accessUser_term_create_name_tip', [1, 256]),
        adGroupMultiNameTip: this.get('service.accessUserGroup_term_create_name_tip', [1, 256]),

        // 表单提示错误提示
        nameFormatErr: this.get('service.accessUserGroup_task_name_valid'),
        adUserNameFormatErr: this.get('service.share_term_createCIFS_addADUser_name_validation'),
        adUserGroupNameFormatErr: this.get('service.share_term_createCIFS_addADUserGroup_name_validation'),

        // 冒泡提示资料
        helpRid: this.get('service.accessUser_task_setSecuPolicy_RID_tip'),

        /**
         * RID 错误范围提示
         */
        ridRange: (min: number, max: number): string => this.get('common.valueRange_label', [min, max]),

        /**
         * 创建用户组结果
         * @param name 用户名
         */
        createGroupResult: (name: string): string => this.get('rest.createWinLocalAuthUserGroup_msg', [name]),


        /**
         * 添加用户组成员
         * @param name 用户名
         */
        addGroupMemberResult: (groupName: string, memberName: string): string =>
            this.get('rest.addMembertoWinLocalAuthGroup_msg', [memberName, groupName]),

        /**
         * 删除用户组结果
         * @param name
         */
        deleteGroupResult: (name: string): string => this.get('rest.deleteWinLocalAuthUserGroup_msg', [name]),

        /**
         * 移除用户组结果
         * @param name
         */
        removeGroupResult: (groupName: string, memberName: string): string =>
            this.get('rest.removeMemberfromWinLocalAuthGroup_msg', [groupName, memberName]),

        /**
         * 修改用户组结构
         */
        modifyGroupResult: (name: string): string => this.get('rest.modifyWinLocalAuthUserGroup_msg', [name]),
    };

    /**
     * 所有特权
     */
    allPermit: Privilege[] = [
        {
            id: '1',
            key: 'SeSecurityPrivilege',
            description: this.i18n.seSecurityDes,
        },
        ...!ProductConfig.isSupportAntivirus ? [] : [
            {
                id: '2',
                key: 'SeAntiVirusPrivilege',
                description: this.i18n.SeAntiVirusPrivilege,
            },
        ],
    ];

    /**
     * 组类型
     * 1 内置组,
     * 2 自定义组
     */
    groupTypeOpt = [
        {
            value: '1',
            label: this.get('service.accessUser_userGroupType_default_value'),
        },
        {
            value: '2',
            label: this.get('service.accessUser_userGroupType_userCreate_value'),
        },
    ];

    constructor(private i18nService: I18nService) { }
}
